import { differenceInDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { BagDetail } from '../types';
import { BagDeadlineBadge } from './BagDeadlineBadge';

type Props = {
  bag: BagDetail;
};

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getAgeLabel = (bag: BagDetail): string => {
  if (bag.label_generated && bag.dispatched_at) {
    const date = format(new Date(bag.dispatched_at), "dd/MM", { locale: ptBR });
    return `etiqueta gerada em ${date}`;
  }
  if (bag.status === 'fechada' && bag.closed_at) {
    const days = differenceInDays(new Date(), new Date(bag.closed_at));
    return `fechada há ${days} ${days === 1 ? 'dia' : 'dias'}`;
  }
  const days = differenceInDays(new Date(), new Date(bag.opened_at));
  return `aberta há ${days} ${days === 1 ? 'dia' : 'dias'}`;
};

const getContact = (customer: BagDetail['customer']): string | null => {
  if (!customer) return null;
  if (customer.instagram) return `@${customer.instagram}`;
  if (customer.whatsapp) return customer.whatsapp;
  return null;
};

export const BagDetailHeader = ({ bag }: Props) => {
  const activeSales = bag.sales.filter((s) => s.payment_status !== 'cancelled');
  const itemsCount = activeSales.reduce(
    (sum, s) => sum + s.sale_items.reduce((qty, i) => qty + i.quantity, 0),
    0
  );
  const total = activeSales.reduce((sum, s) => sum + (s.total ?? 0), 0);
  const ageLabel = getAgeLabel(bag);
  const contact = getContact(bag.customer);
  const name = bag.customer?.name ?? 'Cliente excluído';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg font-semibold">{name}</h2>
        {contact && (
          <span className="text-sm text-muted-foreground">{contact}</span>
        )}
        <BagDeadlineBadge expiresAt={bag.expires_at} />
      </div>
      <p className="text-sm text-muted-foreground">{ageLabel}</p>
      <p className="text-sm font-medium">
        {itemsCount} {itemsCount === 1 ? 'item' : 'itens'} · {formatCurrency(total)}
      </p>
    </div>
  );
};
